"use client"

import type { AlgoliaJobAdsRequest } from "@/modules/jobAds/client/utils/computeAlgoliaJobAdsRequest"
import type { MultipleQueriesQuery, SearchOptions } from "@algolia/client-search"

import classNames from "classnames"
import Image from "next/image"
import { type FunctionComponent, useState } from "react"

import { Button } from "@jobteaser/spark/components/Button"
import { Download } from "@jobteaser/spark/components/Icons/Download"

import { useNotification } from "@/modules/notification/hooks"
import { getAssetsUrl } from "@/modules/staticAssets/assetsManager/assetsManager"

import styles from "./AlgoliaRequestParamsRetriever.module.css"

type AlgoliaRequestParamsRetrieverButtonsProps = {
  algoliaJobAdsRequest: AlgoliaJobAdsRequest
}

export const AlgoliaRequestParamsRetrieverButtons: FunctionComponent<AlgoliaRequestParamsRetrieverButtonsProps> = ({
  algoliaJobAdsRequest,
}) => {
  const [optionsVisible, setOptionsVisible] = useState(false)
  const showNotification = useNotification()

  const handleOptionsClick = (): void => {
    setOptionsVisible(!optionsVisible)
  }

  const retrieveDisplayableAlgoliaParams = (query: MultipleQueriesQuery): SearchOptions => {
    const queryParams = query.params

    if (!queryParams) return {}

    return {
      filters: query.params.filters,
      optionalFilters: query.params.optionalFilters,
    }
  }

  const handleSponsoredButtonClick = (): void => {
    const sponsoredAlgoliaRequest = algoliaJobAdsRequest.sponsoredQuery
    const sponsoredRequestParams = retrieveDisplayableAlgoliaParams(sponsoredAlgoliaRequest)

    navigator.clipboard.writeText(JSON.stringify(sponsoredRequestParams, undefined, 2))
    showNotification({
      content: "Sponsored params copied!",
      variant: "success",
    })
  }

  const handleMainButtonClick = (): void => {
    const mainAlgoliaRequest = algoliaJobAdsRequest.mainQuery
    const mainRequestParams = retrieveDisplayableAlgoliaParams(mainAlgoliaRequest)

    navigator.clipboard.writeText(JSON.stringify(mainRequestParams, undefined, 2))
    showNotification({
      content: "Main params copied!",
      variant: "success",
    })
  }

  return (
    <div className={styles.main}>
      <Button
        icon={Download}
        variant="primary"
        onBackground="light"
        title="Copy Algolia configuration"
        onClick={handleOptionsClick}
      >
        <Image src={`${getAssetsUrl()}/jobAds/client/duck-algolia.png`} alt="Algolia Icon" width={20} height={20} />
      </Button>
      <ul
        className={classNames(styles.options, {
          [styles["options--visible"]]: optionsVisible,
        })}
      >
        <li>
          <Button
            variant="primary"
            onBackground="light"
            title="Copy sponsored params"
            onClick={handleSponsoredButtonClick}
          >
            Sponsored params
          </Button>
        </li>
        <li>
          <Button variant="primary" onBackground="light" title="Copy main params" onClick={handleMainButtonClick}>
            Main params
          </Button>
        </li>
      </ul>
    </div>
  )
}
